/**
 * audioService.js
 *
 * Spatial audio for the Matterport tour.
 * Each zone in locationAudioConfig.json is a 3D sound SOURCE.
 * Distance-based gain is calculated manually in updateListener() —
 * no PannerNode (proven unreliable across browsers/iframes).
 * Only nearby sources are audible; volume rises as you approach, fades as you leave.
 *
 * Usage:
 *   audioService.fetchAndLoadConfig(url)         — fetch JSON from public/ at runtime (no rebuild)
 *   audioService.reloadConfig()                  — re-fetch + re-apply without page refresh
 *   audioService.startAll()                      — call after first camera position (resumes AudioContext)
 *   audioService.updateListener(pos, rotation)   — call on camera move (throttled to 100ms)
 *   audioService.getZonesWithMarkers()           — returns zones with showMarker: true
 *   audioService.getZones()                      — returns all zone objects
 *   audioService.setEnabled(false)               — mute / unmute
 *   audioService.setGlobalVolume(0–1)            — master volume
 *   audioService.updateZoneConfig(id, updates)   — live-patch a zone's params (from debug panel)
 *   audioService.onActiveZoneChange(cb)          — subscribe to nearest-zone changes
 *   audioService.offActiveZoneChange(cb)         — unsubscribe
 *   audioService.getActiveZone()                 — current nearest zone (or null)
 *   audioService.stop()                          — full teardown on unmount
 *
 * audioSrc values:
 *   "TEST_TONE"              → oscillator drone (no file needed, plays immediately)
 *   "/assets/audio/x.mp3"   → decoded via Web Audio API from public/assets/audio/
 */

import bundleHotspotService from './bundleHotspotService';
import { assetUrl } from '../utils/assetUrl';

// Extra attenuation applied to the ambient music when streaming on a mobile
// device. Mobile phone speakers emphasize the ambient track so it feels
// noticeably louder than on desktop — halve it to match perceived loudness.
// (Effective ambient gain on mobile = config.volume × this × globalVolume.)
const MOBILE_AMBIENT_REDUCTION = 0.5;

// Additional ducking factor applied to the mobile ambient baseline while a
// narration / voiceover is playing. 0.35 means the music drops to 35% of
// its already-halved mobile baseline so the narration sits clearly on top
// without making the music feel completely absent.
const MOBILE_AMBIENT_NARRATION_DUCK = 0.35;

class AudioService {
  constructor() {
    this._zones        = [];
    this._globalVolume = 0;
    this._enabled      = true;
    this._audioCtx     = null;
    this._masterGain   = null;
    this._sources      = new Map(); // zoneId → { gainNode, zone, osc1?, osc2?, bufferSource? }
    this._mediaSources = new Map(); // zoneId → MediaElementAudioSourceNode (persisted across unregister)
    this._logCounter   = 0;
    this._started      = false;
    this._configUrl    = null;

    // Audio start gate — when false, startAll() is a no-op so background
    // music does NOT begin during scene loading. Flipped to true by
    // audioService.allowStart() which is called from the Welcome screen's
    // "Start Tour" button so the tour begins with sound only after the
    // user explicitly enters the experience. Any startAll() requests that
    // arrive while gated are remembered and replayed on allowStart().
    this._startAllowed = false;
    this._pendingStart = false;

    // Identifier of the narration currently being played by
    // playTourNarration() — used by the POI speaker button to reflect
    // "playing" state when the footer quick-nav is narrating a stop
    // whose key matches a POI's voiceoverTagId.
    this._currentNarrationKey = null;
    // Variant of the active narration — 'connected' (guided tour) or
    // 'standalone' (quick-nav single stop). Parsed from the audio file
    // path so the read-along captions can pick the matching text variant
    // from navigationStopsGuide.json.
    this._currentNarrationVariant = null;

    // Active zone event system
    this._currentActiveZoneId = null;
    this._activeZoneListeners = [];

    // Track last known listener position/rotation so live config edits can re-evaluate immediately
    this._lastPosition = null;
    this._lastRotation = null;

    // Ambient track
    this._ambientConfig = null;   // parsed from config.ambient
    this._ambientGain   = null;   // GainNode: bufferSource → ambientGain → masterGain
    this._ambientSource = null;   // AudioBufferSourceNode
    this._ambientDucked = false;  // guards against ramp-spam on every updateListener tick

    // Voiceover
    this._voiceoverSource = null;  // AudioBufferSourceNode for current voiceover
    this._voiceoverGain   = null;  // GainNode for voiceover with fade envelope
    this._voiceoverDucked = false; // true while voiceover is ducking the bg music
    // Playback-position tracking so callers (voiceoverService) can
    // implement resume-from-pause for proximity-triggered narrations.
    this._voiceoverStartCtxTime = 0;  // AudioContext time when current
                                      // BufferSource was .start()ed
    this._voiceoverStartOffset  = 0;  // offset (seconds into buffer)
                                      // passed to that .start() call

    // Global mute state (applies to all sound from the app, including voiceover)
    this._muted = false;

    // Visibility gating — when the tab/app is backgrounded (user switches
    // apps on mobile, locks the screen, switches browser tabs, etc.) we
    // pause all audio and resume it only when the page becomes visible
    // again. Tracked separately from _muted so coming back to the tab
    // does not override an explicit user mute.
    this._pausedByVisibility = false;
    if (typeof document !== 'undefined') {
      this._visibilityHandler = () => {
        if (document.hidden) this._handleHidden();
        else this._handleVisible();
      };
      document.addEventListener('visibilitychange', this._visibilityHandler);
      // Safari iOS sometimes only fires pagehide / pageshow on app switch.
      window.addEventListener('pagehide', () => this._handleHidden());
      window.addEventListener('pageshow', () => {
        if (!document.hidden) this._handleVisible();
      });
      // Backgrounding via blur on some Android browsers.
      window.addEventListener('blur', () => {
        if (document.hidden) this._handleHidden();
      });
    }

    window._audioService = this;
  }

  /** Returns true when all app audio is globally muted. */
  isMuted() {
    return this._muted;
  }

  /**
   * Toggle global mute. Muting PAUSES everything so it can resume in place
   * when unmuted: HTMLAudioElement-based tour narration is pause()'d and the
   * Web Audio context is suspended (which pauses voiceover, ambient music,
   * and all spatial zone sources together). Unmuting resumes all of them.
   */
  setMuted(muted) {
    const next = !!muted;
    if (next === this._muted) return;
    this._muted = next;

    if (this._muted) {
      // Pause the HTMLAudioElement-based tour narration so it can resume later.
      if (this._narrationEl && !this._narrationEl.paused) {
        try { this._narrationEl.pause(); } catch (_) {}
        this._narrationPausedByMute = true;
      }
      if (this._narrationFallbackEl && !this._narrationFallbackEl.paused) {
        try { this._narrationFallbackEl.pause(); } catch (_) {}
        this._narrationFallbackPausedByMute = true;
      }

      // Mobile: ambient music streams via a plain HTMLAudioElement that
      // bypasses the Web Audio masterGain, so suspending the AudioContext
      // does NOT silence it. Pause the element directly.
      if (this._ambientHtmlEl && !this._ambientHtmlEl.paused) {
        try { this._ambientHtmlEl.pause(); } catch (_) {}
        this._ambientHtmlPausedByMute = true;
      }

      // Save the current global volume so we can restore exactly the
      // same level on unmute. Suspending the AudioContext stops audio
      // from advancing; we don't need to actually drop the volume to 0
      // since suspended sources are silent regardless.
      this._volumeBeforeMute = this._globalVolume;
      if (this._audioCtx && this._audioCtx.state === 'running') {
        this._audioCtx.suspend().catch(() => {});
      }
    } else {
      // Restore the AudioContext first so resumed audio is audible
      // immediately at exactly the volume the user had before muting.
      if (this._audioCtx && this._audioCtx.state === 'suspended') {
        this._audioCtx.resume().catch(() => {});
      }
      if (typeof this._volumeBeforeMute === 'number') {
        this.setGlobalVolume(this._volumeBeforeMute);
        this._volumeBeforeMute = null;
      }

      // Resume any narration we paused on mute.
      if (this._narrationPausedByMute && this._narrationEl) {
        this._narrationEl.play().catch((e) => console.warn('🔊 narration resume failed:', e));
        this._narrationPausedByMute = false;
      }
      if (this._narrationFallbackPausedByMute && this._narrationFallbackEl) {
        this._narrationFallbackEl.play().catch(() => {});
        this._narrationFallbackPausedByMute = false;
      }

      // Resume the mobile streaming ambient element if we paused it on mute.
      if (this._ambientHtmlPausedByMute && this._ambientHtmlEl) {
        this._ambientHtmlEl.play().catch(() => {});
        this._ambientHtmlPausedByMute = false;
      }
    }

    try { window.dispatchEvent(new CustomEvent('audioMuted', { detail: { muted: this._muted } })); } catch (_) {}
  }

  // ─── Visibility gating ────────────────────────────────────────────────────
  // When the page is hidden (user switches apps on mobile, locks the
  // screen, switches tabs) we silence everything in place; when the page
  // becomes visible again we resume what we paused. We never override
  // an explicit user mute — _pausedByVisibility tracks only the audio
  // we silenced ourselves.

  _handleHidden() {
    if (this._pausedByVisibility) return;
    if (this._muted) return; // already silent — nothing to do
    this._pausedByVisibility = true;

    // Pause HTMLAudioElement-based narration so it can resume in place.
    if (this._narrationEl && !this._narrationEl.paused) {
      try { this._narrationEl.pause(); } catch (_) {}
      this._narrationPausedByVisibility = true;
    }
    if (this._narrationFallbackEl && !this._narrationFallbackEl.paused) {
      try { this._narrationFallbackEl.pause(); } catch (_) {}
      this._narrationFallbackPausedByVisibility = true;
    }
    // Mobile streaming ambient bypasses the AudioContext — pause it directly.
    if (this._ambientHtmlEl && !this._ambientHtmlEl.paused) {
      try { this._ambientHtmlEl.pause(); } catch (_) {}
      this._ambientHtmlPausedByVisibility = true;
    }
    // Suspend the Web Audio context — pauses voiceover, ambient (desktop),
    // and all spatial zone sources together so they can resume in place.
    if (this._audioCtx && this._audioCtx.state === 'running') {
      this._audioCtx.suspend().catch(() => {});
    }
    console.log('🔇 AudioService: page hidden — paused all audio');
  }

  _handleVisible() {
    if (!this._pausedByVisibility) return;
    this._pausedByVisibility = false;
    if (this._muted) return; // user explicitly muted — leave silent

    if (this._audioCtx && this._audioCtx.state === 'suspended') {
      this._audioCtx.resume().catch(() => {});
    }
    if (this._narrationPausedByVisibility && this._narrationEl) {
      this._narrationEl.play().catch(() => {});
      this._narrationPausedByVisibility = false;
    }
    if (this._narrationFallbackPausedByVisibility && this._narrationFallbackEl) {
      this._narrationFallbackEl.play().catch(() => {});
      this._narrationFallbackPausedByVisibility = false;
    }
    if (this._ambientHtmlPausedByVisibility && this._ambientHtmlEl) {
      this._ambientHtmlEl.play().catch(() => {});
      this._ambientHtmlPausedByVisibility = false;
    }
    console.log('🔊 AudioService: page visible — resumed audio');
  }

  // ─── Configuration ────────────────────────────────────────────────────────

  loadConfig(config) {
    this._zones        = (config.zones || []).map(z => ({ ...z })); // shallow clone each zone
    this._globalVolume = config.globalVolume ?? 0.7;
    this._ambientConfig = config.ambient ? { ...config.ambient } : null;
    console.log(`🔊 AudioService: loaded ${this._zones.length} spatial audio zones` +
      (this._ambientConfig?.enabled ? ' + ambient track' : ''));

    // Pre-fetch the ambient audio file as soon as the config is known so
    // the bytes are ready in memory by the time the user hits "Start Tour".
    // Without this, _startAmbient() incurs a full HTTP fetch + decode after
    // the gesture, delaying audible playback by hundreds of ms (or seconds
    // on slow networks). The decoded AudioBuffer is created lazily on first
    // _startAmbient() call (decodeAudioData needs an AudioContext).
    //
    // On mobile devices we skip the eager prefetch to conserve memory —
    // the ArrayBuffer (~3-5MB) is fetched on demand in _startAmbient().
    this._ambientFetchPromise = null;
    this._ambientArrayBuffer  = null;
    // Force the desktop audio pipeline on mobile too. The mobile-specific
    // streaming path (HTMLAudioElement + MediaElementSource + GainNode)
    // produces near-silent or zero output on iPhone Chrome — the user
    // confirmed that switching to "Request Desktop Site" (which flips
    // this UA check to false) makes all audio work correctly. Decoded
    // AudioBuffer playback uses ~150 MB on a 14 MB MP3, which is fine
    // on iPhone 12+ but may strain iPhone 11 / older 3 GB devices.
    const isMobile = false;
    if (!isMobile && this._ambientConfig?.enabled && this._ambientConfig.audioSrc) {
      this._ambientFetchPromise = (async () => {
        try {
          const response = await fetch(assetUrl(this._ambientConfig.audioSrc));
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          this._ambientArrayBuffer = await response.arrayBuffer();
          console.log(`🌿 AudioService: ambient prefetched (${(this._ambientArrayBuffer.byteLength / 1024).toFixed(0)} KB)`);
        } catch (err) {
          console.warn('🌿 AudioService: ambient prefetch failed:', err?.message);
        }
      })();
    }

    // Mobile: create the streaming HTMLAudioElement immediately so the
    // browser starts downloading and buffering the MP3 while the loading
    // screen is on screen. We start it MUTED + autoplaying — muted
    // autoplay is allowed on iOS Safari / Chrome Android without a
    // gesture, which forces the browser to actively fetch and decode
    // the file (preload="auto" alone is best-effort and often delayed).
    // By the time the user taps Start Tour the bytes are already in
    // memory and we just unmute + ramp volume — audible playback is
    // effectively instant.
    if (isMobile && this._ambientConfig?.enabled && this._ambientConfig.audioSrc) {
      try {
        if (!this._ambientHtmlEl) {
          const el = new Audio();
          el.loop = true;
          el.preload = 'auto';
          el.playsInline = true;
          el.muted = true; // muted autoplay is permitted without a user gesture
          el.setAttribute('playsinline', '');
          el.setAttribute('webkit-playsinline', '');
          el.setAttribute('muted', '');
          el.volume = 0; // ramped up on Start Tour
          el.src = assetUrl(this._ambientConfig.audioSrc);
          el.load();
          this._ambientHtmlEl = el;
          // Kick off muted playback now so buffering happens during the
          // welcome screen rather than after the user taps Start Tour.
          const p = el.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {
              console.log('🌿 AudioService: ambient muted-autoplay started on mobile');
            }).catch((err) => {
              // Some browsers still block muted autoplay until first
              // touch — fall back to a one-shot gesture listener.
              console.warn('🌿 AudioService: muted autoplay deferred:', err?.message);
              const retry = () => {
                document.removeEventListener('touchstart', retry, true);
                document.removeEventListener('click', retry, true);
                el.play().catch(() => {});
              };
              document.addEventListener('touchstart', retry, true);
              document.addEventListener('click', retry, true);
            });
          }
          console.log('🌿 AudioService: ambient element preloading on mobile');
        }
      } catch (err) {
        console.warn('🌿 AudioService: ambient mobile preload failed:', err?.message);
      }
    }
  }

  /** Returns true while the ambient music BufferSource is actually playing. */
  isAmbientPlaying() {
    return !!this._ambientSource;
  }

  /**
   * Fetch config JSON from the given URL (e.g. '/config/locationAudioConfig.json')
   * and call loadConfig(). Stores the URL for later reloadConfig() calls.
   */
  async fetchAndLoadConfig(url) {
    this._configUrl = url;
    try {
      const response = await fetch(url + '?t=' + Date.now()); // bust cache on every load
      if (!response.ok) throw new Error(`HTTP ${response.status} fetching ${url}`);
      const config = await response.json();
      this.loadConfig(config);
    } catch (err) {
      console.error('🔊 AudioService: failed to fetch config from', url, err);
      throw err;
    }
  }

  /**
   * Re-fetch config from the stored URL and apply it live.
   * If audio has already started, tears down all zone sources and restarts them
   * so new audioSrc, volume, loop etc. take effect immediately.
   */
  async reloadConfig() {
    if (!this._configUrl) {
      console.warn('🔊 AudioService: no configUrl set — call fetchAndLoadConfig first');
      return;
    }
    console.log('🔊 AudioService: reloading config from', this._configUrl);
    await this.fetchAndLoadConfig(this._configUrl);

    if (this._started && this._audioCtx) {
      await this._restartZones();
    }

    // Re-evaluate gains immediately with last known position
    if (this._lastPosition) {
      this.updateListener(this._lastPosition, this._lastRotation);
    }
  }

  /** Returns zones that want a visible sphere marker placed in the scene. */
  getZonesWithMarkers() {
    return this._zones.filter(z => z.showMarker);
  }

  /** Returns all zone objects. */
  getZones() {
    return this._zones;
  }

  /**
   * Returns a snapshot of the ambient track state for the debug panel UI.
   * Polled at ~500ms — no events needed.
   */
  getAmbientStatus() {
    const cfg = this._ambientConfig;
    if (!cfg?.enabled) return { configured: false };
    return {
      configured:   true,
      playing:      !!this._ambientSource,
      ducked:       this._ambientDucked,
      currentGain:  this._ambientGain ? this._ambientGain.gain.value : null,
      targetVolume: cfg.volume       ?? 0.4,
      duckedVolume: cfg.duckedVolume ?? 0.05,
    };
  }

  // ─── Active zone event system ─────────────────────────────────────────────

  /**
   * Subscribe to active zone changes. Callback receives the zone object (or null when silent).
   * @param {(zone: object|null) => void} cb
   */
  onActiveZoneChange(cb) {
    if (typeof cb === 'function' && !this._activeZoneListeners.includes(cb)) {
      this._activeZoneListeners.push(cb);
    }
  }

  offActiveZoneChange(cb) {
    this._activeZoneListeners = this._activeZoneListeners.filter(f => f !== cb);
  }

  /** Returns the currently active (nearest) zone object, or null. */
  getActiveZone() {
    if (!this._currentActiveZoneId) return null;
    return this._zones.find(z => z.id === this._currentActiveZoneId) || null;
  }

  // ─── Tour narration duck / unduck ─────────────────────────────────────────

  /**
   * Compute the current target volume for the mobile streaming ambient
   * <audio> element. `ducked` selects the narration-duck level (further
   * reduced by MOBILE_AMBIENT_NARRATION_DUCK) vs the normal mobile
   * baseline.
   *
   * `forGain=true` returns the value that should be applied to the
   * MediaElementSource → GainNode (masterGain handles globalVolume there,
   * so we omit it to avoid double-attenuation). `forGain=false` returns
   * the value to set on HTMLAudioElement.volume directly (no masterGain
   * in the path, so globalVolume must be baked in).
   */
  _mobileAmbientTarget(ducked, forGain = false) {
    const cfg = this._ambientConfig || {};
    let target = (cfg.volume ?? 0.4) * MOBILE_AMBIENT_REDUCTION;
    if (ducked) target *= MOBILE_AMBIENT_NARRATION_DUCK;
    if (!forGain) {
      target *= (this._enabled ? this._globalVolume : 0);
    }
    return Math.max(0, Math.min(1, target));
  }

  /**
   * Smoothly ramp the mobile ambient to a baseline (`ducked=false`) or
   * narration-ducked (`ducked=true`) level over `duration` seconds.
   * Uses the Web Audio GainNode when wired (iOS-safe) and falls back
   * to ramping HTMLAudioElement.volume otherwise.
   */
  _rampMobileAmbient(ducked, duration) {
    if (this._ambientMobileGain && this._audioCtx) {
      const target = this._mobileAmbientTarget(ducked, true);
      const t = this._audioCtx.currentTime;
      const g = this._ambientMobileGain.gain;
      g.cancelScheduledValues(t);
      g.setValueAtTime(g.value, t);
      g.linearRampToValueAtTime(target, t + Math.max(0.05, duration));
    } else if (this._ambientHtmlEl) {
      const target = this._mobileAmbientTarget(ducked, false);
      this._rampHtmlVolume(this._ambientHtmlEl, target, duration);
    }
  }

  /**
   * Duck ambient audio to a soft level when tour narration starts.
   * Keeps background music subtly present beneath the voiceover.
   */
  duckForTour() {
    this._tourNarrationActive = true;
    if (!this._ambientConfig?.enabled) return;
    const fade = this._ambientConfig.tourDuckFadeTime  ?? 1.0;
    // Mobile streaming path: ramp element.volume directly using the
    // mobile-specific narration duck level (baseline × 0.2 by default).
    if (this._ambientHtmlEl && !this._ambientGain) {
      this._rampMobileAmbient(true, fade);
      console.log(`🎵 Tour: ducking ambient (mobile) over ${fade}s`);
      return;
    }
    if (!this._ambientGain) return;
    const ctx = this._audioCtx;
    if (!ctx) return;
    const target = this._ambientConfig.tourDuckedVolume ?? 0.15;
    const now = ctx.currentTime;
    const ag = this._ambientGain.gain;
    ag.cancelScheduledValues(now);
    ag.setValueAtTime(ag.value, now);
    ag.linearRampToValueAtTime(target, now + fade);
    console.log(`🎵 Tour: ducking ambient to ${target.toFixed(2)} over ${fade}s`);
  }

  /**
   * Restore ambient audio after tour narration ends.
   * Makes music more prominent during transitions between tour stops.
   * Only restores if spatial-zone ducking is not also active.
   */
  unduckForTour() {
    this._tourNarrationActive = false;
    if (!this._ambientConfig?.enabled) return;
    if (this._ambientDucked) return; // spatial zone is still ducking — leave it
    const fade = this._ambientConfig.tourUnduckFadeTime ?? 2.0;
    if (this._ambientHtmlEl && !this._ambientGain) {
      this._rampMobileAmbient(false, fade);
      console.log(`🎵 Tour: restoring ambient (mobile) over ${fade}s`);
      return;
    }
    if (!this._ambientGain) return;
    const ctx = this._audioCtx;
    if (!ctx) return;
    const target = this._ambientConfig.volume ?? 0.4;
    const now = ctx.currentTime;
    const ag = this._ambientGain.gain;
    ag.cancelScheduledValues(now);
    ag.setValueAtTime(ag.value, now);
    ag.linearRampToValueAtTime(target, now + fade);
    console.log(`🎵 Tour: restoring ambient to ${target.toFixed(2)} over ${fade}s`);
  }

  /** Linear ramp on an HTMLAudioElement.volume over `duration` seconds. */
  _rampHtmlVolume(el, target, duration) {
    if (!el) return;
    const start = el.volume;
    const end = Math.max(0, Math.min(1, target));
    const startTime = performance.now();
    const dur = Math.max(0.05, duration) * 1000;
    if (this._ambientRampRaf) cancelAnimationFrame(this._ambientRampRaf);
    const tick = () => {
      const t = Math.min(1, (performance.now() - startTime) / dur);
      el.volume = start + (end - start) * t;
      if (t < 1) this._ambientRampRaf = requestAnimationFrame(tick);
      else this._ambientRampRaf = null;
    };
    this._ambientRampRaf = requestAnimationFrame(tick);
  }

  /**
   * Play a tour narration audio file.
   * Routes through the existing AudioContext so autoplay policy cannot block it.
   * Creates a single persistent HTMLAudioElement + MediaElementAudioSourceNode reused
   * across all narration calls.
   *
   * @param {string} src - Full URL or path to the MP3
   * @param {() => void} [onEnded] - Called when playback finishes naturally
   * @param {() => void} [onError] - Called when the file cannot be loaded
   * @param {string}      [key]    - Optional narration identifier (e.g. a
   *   stop's `narrationKey`). Exposed via getCurrentNarrationKey() so other
   *   UI (POI speaker buttons) can reflect "playing" state.
   */
  playTourNarration(src, onEnded, onError, key) {
    // Stop any currently playing narration first
    this._stopNarrationElement();
    // Make sure no in-flight fade leaves the next clip starting silent.
    if (this._narrationFadeTimer) {
      clearInterval(this._narrationFadeTimer);
      this._narrationFadeTimer = null;
    }
    if (this._narrationEl) { try { this._narrationEl.volume = 1; } catch (_) {} }
    this._currentNarrationKey = key || null;
    this._currentNarrationVariant =
      (/_([a-z]+)_(?:en|hi|mr)\.mp3(?:\?|$)/i.exec(src || '')?.[1])
      || (/\b(connected|standalone)\b/i.exec(src || '')?.[1])
      || null;
    if (typeof window !== 'undefined') {
      try { window.dispatchEvent(new CustomEvent('narrationStarted', { detail: { key: this._currentNarrationKey, variant: this._currentNarrationVariant } })); } catch (_) {}
    }

    // Cross-system handoff: only ONE narration plays at a time. If a POI
    // voiceover is currently playing, fade it out smoothly so the new
    // tour narration doesn't overlap. Reach the voiceoverService through
    // its window singleton to avoid a circular import.
    try {
      const vo = (typeof window !== 'undefined') ? window._voiceoverService : null;
      if (vo && vo.isPlaying?.()) {
        if (typeof vo.fadeOut === 'function') vo.fadeOut(0.4);
        else vo.stop?.();
      }
    } catch (_) { /* ignore */ }

    const ctx = this._audioCtx;

    // Always ensure an AudioContext exists — even for cross-origin sources.
    // Calling ctx.resume() before audio.play() inherits the user-activation
    // that was established by unlockOnGesture(), which prevents the
    // NotAllowedError that plagues the plain-Audio fallback on mobile.
    const useCtx = (ctx && ctx.state !== 'closed') ? ctx : this._getOrCreateCtx();

    // Cross-origin narration (CDN) goes through the AudioContext path
    // too — the CDN serves Access-Control-Allow-Origin: *, so the
    // crossOrigin='anonymous' element loads cleanly and the
    // MediaElementSource → masterGain wiring produces audible output.
    // The plain-Audio fallback below is reserved for the case where
    // the AudioContext can't be created at all (no useCtx).
    if (useCtx && useCtx.state !== 'closed') {
      // Ensure masterGain exists so narration can be muted via setGlobalVolume/setMuted
      if (!this._masterGain) {
        this._masterGain = useCtx.createGain();
        this._masterGain.gain.value = this._enabled ? this._globalVolume : 0;
        this._masterGain.connect(useCtx.destination);
      }

      // Create the long-lived Audio element and connect it to the AudioContext once
      if (!this._narrationEl) {
        this._narrationEl = new Audio();
        this._narrationEl.crossOrigin = 'anonymous';
        try {
          this._narrationSource = useCtx.createMediaElementSource(this._narrationEl);
          // Route through masterGain so the global mute button silences narration too
          this._narrationSource.connect(this._masterGain);
        } catch (e) {
          // createMediaElementSource throws if called twice on the same element
          console.warn('🔊 Tour narration: MediaElementSource already exists', e);
        }
      }
      const audio = this._narrationEl;
      audio.src = src;
      audio.load(); // Explicit load — required by some mobile browsers after src change
      let ended = false;
      this._narrationSafetyTimer = null;
      const fireEnded = () => {
        if (ended) return;
        ended = true;
        if (this._narrationSafetyTimer) { clearTimeout(this._narrationSafetyTimer); this._narrationSafetyTimer = null; }
        audio.ontimeupdate = null;
        this._currentNarrationKey = null;
        if (onEnded) onEnded();
      };
      audio.onended = fireEnded;
      // Safety net: on some mobile browsers, `ended` doesn't fire reliably
      // when the element is routed through createMediaElementSource.
      // Poll via timeupdate and trigger manually when playback finishes.
      audio.ontimeupdate = () => {
        if (audio.duration && audio.currentTime >= audio.duration - 0.05) {
          fireEnded();
        }
      };
      // Duration-based safety: once we know the length, set a hard ceiling
      // so the tour can never get stuck waiting for an ended event.
      const onMeta = () => {
        audio.removeEventListener('loadedmetadata', onMeta);
        if (!ended && audio.duration && isFinite(audio.duration)) {
          this._narrationSafetyTimer = setTimeout(fireEnded, (audio.duration * 1000) + 3000);
        }
      };
      audio.addEventListener('loadedmetadata', onMeta);
      audio.onerror = () => {
        ended = true;
        if (this._narrationSafetyTimer) { clearTimeout(this._narrationSafetyTimer); this._narrationSafetyTimer = null; }
        audio.removeEventListener('loadedmetadata', onMeta);
        audio.ontimeupdate = null;
        this._currentNarrationKey = null;
        if (onError) onError();
      };
      if (this._muted) {
        // Queue narration but don't start it while muted — unmuting will play() it.
        this._narrationPausedByMute = true;
      } else {
        // Resume context and play — works if context was created in a gesture stack
        useCtx.resume().then(() => audio.play()).catch(e => {
          console.warn('🔊 Tour narration play failed:', e);
          if (onError) onError();
        });
      }
    } else {
      // AudioContext not yet started — try standard HTMLAudioElement as fallback
      const audio = new Audio(src);
      this._narrationFallbackEl = audio;
      let ended = false;
      this._narrationFallbackSafetyTimer = null;
      const fireEnded = () => {
        if (ended) return;
        ended = true;
        if (this._narrationFallbackSafetyTimer) { clearTimeout(this._narrationFallbackSafetyTimer); this._narrationFallbackSafetyTimer = null; }
        audio.ontimeupdate = null;
        this._currentNarrationKey = null;
        if (onEnded) onEnded();
      };
      audio.onended = fireEnded;
      audio.ontimeupdate = () => {
        if (audio.duration && audio.currentTime >= audio.duration - 0.05) {
          fireEnded();
        }
      };
      const onMeta = () => {
        audio.removeEventListener('loadedmetadata', onMeta);
        if (!ended && audio.duration && isFinite(audio.duration)) {
          this._narrationFallbackSafetyTimer = setTimeout(fireEnded, (audio.duration * 1000) + 3000);
        }
      };
      audio.addEventListener('loadedmetadata', onMeta);
      audio.onerror = () => {
        ended = true;
        if (this._narrationFallbackSafetyTimer) { clearTimeout(this._narrationFallbackSafetyTimer); this._narrationFallbackSafetyTimer = null; }
        audio.removeEventListener('loadedmetadata', onMeta);
        audio.ontimeupdate = null;
        this._currentNarrationKey = null;
        if (onError) onError();
      };
      if (this._muted) {
        this._narrationFallbackPausedByMute = true;
      } else {
        // Resume AudioContext first to inherit user-activation on mobile,
        // then play. This prevents NotAllowedError on cross-origin narration.
        const playPromise = useCtx
          ? useCtx.resume().then(() => audio.play())
          : audio.play();
        playPromise.catch(e => {
          console.warn('🔊 Tour narration fallback play failed:', e);
          if (onError) onError();
        });
      }
    }
  }

  /** Stop tour narration immediately (no fade). */
  stopTourNarration() {
    this._stopNarrationElement();
  }

  /**
   * Smoothly fade out the currently playing tour narration over
   * `durationSec` and then stop it. Used to cross-fade when another
   * narration source (e.g. a POI voiceover click) is about to start,
   * so the user never hears two voices overlapping.
   */
  fadeOutTourNarration(durationSec = 0.4) {
    const els = [this._narrationEl, this._narrationFallbackEl].filter(
      (el) => el && !el.paused && el.src
    );
    if (els.length === 0) {
      this._currentNarrationKey = null;
      return;
    }
    // Cancel any prior fade so back-to-back interruptions don't fight.
    if (this._narrationFadeTimer) {
      clearInterval(this._narrationFadeTimer);
      this._narrationFadeTimer = null;
    }
    // Clear the key right away so UI indicators (POI speaker button)
    // release their "playing" state during the fade.
    this._currentNarrationKey = null;
    const startVolumes = els.map((el) => el.volume);
    const steps = Math.max(4, Math.round((durationSec * 1000) / 40));
    let i = 0;
    this._narrationFadeTimer = setInterval(() => {
      i += 1;
      const t = i / steps;
      els.forEach((el, idx) => {
        try { el.volume = Math.max(0, startVolumes[idx] * (1 - t)); } catch (_) {}
      });
      if (i >= steps) {
        clearInterval(this._narrationFadeTimer);
        this._narrationFadeTimer = null;
        // Fully stop and restore default volume so the next narration
        // play() starts at full level.
        this._stopNarrationElement();
        els.forEach((el) => { try { el.volume = 1; } catch (_) {} });
        // Restore bg music if the narration had ducked it.
        try { this.unduckForTour?.(); } catch (_) {}
      }
    }, 40);
  }

  /**
   * Identifier of the narration currently playing via playTourNarration(),
   * or null when no narration is active. Set by the most recent
   * playTourNarration() call's `key` argument and cleared on natural end,
   * error, or explicit stop.
   */
  getCurrentNarrationKey() {
    return this._currentNarrationKey || null;
  }

  /**
   * Variant ('connected' | 'standalone') of the narration currently
   * playing via playTourNarration(), or null when none is active.
   */
  getCurrentNarrationVariant() {
    return this._currentNarrationVariant || null;
  }

  /**
   * Returns the HTMLAudioElement currently used for tour narration
   * (either the AudioContext-routed element or the plain fallback),
   * or null when no narration is active. Used by the read-along
   * captions to read currentTime / duration / pause state from the
   * real playback source.
   */
  getNarrationAudio() {
    if (this._narrationEl && this._narrationEl.src) return this._narrationEl;
    if (this._narrationFallbackEl && this._narrationFallbackEl.src) return this._narrationFallbackEl;
    return null;
  }

  /** Pause tour narration in place (resumable). */
  pauseTourNarration() {
    if (this._narrationEl && !this._narrationEl.paused) {
      try { this._narrationEl.pause(); } catch (_) {}
    }
    if (this._narrationFallbackEl && !this._narrationFallbackEl.paused) {
      try { this._narrationFallbackEl.pause(); } catch (_) {}
    }
  }

  /** Resume paused tour narration. */
  resumeTourNarration() {
    if (this._muted) return; // honour global mute
    if (this._narrationEl && this._narrationEl.paused && this._narrationEl.src) {
      this._narrationEl.play().catch(() => {});
    }
    if (this._narrationFallbackEl && this._narrationFallbackEl.paused) {
      this._narrationFallbackEl.play().catch(() => {});
    }
  }

  _stopNarrationElement() {
    const hadKey = !!this._currentNarrationKey;
    this._currentNarrationKey = null;
    this._currentNarrationVariant = null;
    if (hadKey && typeof window !== 'undefined') {
      try { window.dispatchEvent(new CustomEvent('narrationEnded')); } catch (_) {}
    }
    if (this._narrationSafetyTimer) { clearTimeout(this._narrationSafetyTimer); this._narrationSafetyTimer = null; }
    if (this._narrationFallbackSafetyTimer) { clearTimeout(this._narrationFallbackSafetyTimer); this._narrationFallbackSafetyTimer = null; }
    if (this._narrationEl) {
      this._narrationEl.pause();
      this._narrationEl.onended = null;
      this._narrationEl.onerror = null;
      this._narrationEl.ontimeupdate = null;
      this._narrationEl.src = '';
    }
    if (this._narrationFallbackEl) {
      this._narrationFallbackEl.pause();
      this._narrationFallbackEl.onended = null;
      this._narrationFallbackEl.onerror = null;
      this._narrationFallbackEl.ontimeupdate = null;
      this._narrationFallbackEl = null;
    }
  }

  _notifyActiveZoneChange(zone) {
    for (const cb of this._activeZoneListeners) {
      try { cb(zone); } catch (_) {}
    }
  }

  // ─── Live config patch (debug panel) ─────────────────────────────────────

  /**
   * Merge `updates` into a zone's config and apply immediately.
   * Handles: volume, refDistance, maxDistance, rolloffFactor, loop, showMarker,
   *          markerColor, markerRadius, position.x/y/z, audioSrc.
   */
  updateZoneConfig(zoneId, updates) {
    const zone = this._zones.find(z => z.id === zoneId);
    if (!zone) return;

    // Track which visual properties are being updated (before mutations)
    const hasPositionUpdate = !!updates.position;
    const hasColorUpdate    = updates.markerColor  !== undefined;
    const hasRadiusUpdate   = updates.markerRadius !== undefined;

    // Deep-merge position sub-object
    if (updates.position) {
      zone.position = { ...zone.position, ...updates.position };
      delete updates.position;
    }
    Object.assign(zone, updates);

    // Sync the zone reference stored inside the source entry too
    const src = this._sources.get(zoneId);
    if (src) {
      src.zone = zone;

      // Re-evaluate gain via updateListener so distance + new params are applied correctly
      if (this._audioCtx && src.gainNode && this._lastPosition) {
        this.updateListener(this._lastPosition, this._lastRotation);
      }
    }

    // Push visual changes to the Three.js mesh/material in the scene
    if (hasPositionUpdate || hasColorUpdate || hasRadiusUpdate) {
      const visualUpdates = {};
      if (hasPositionUpdate) visualUpdates.position    = zone.position;
      if (hasColorUpdate)    visualUpdates.markerColor  = zone.markerColor;
      if (hasRadiusUpdate)   visualUpdates.markerRadius = zone.markerRadius;
      bundleHotspotService.updateHotspot(zone.label, visualUpdates);
    }
  }

  // ─── Start all spatial sources ────────────────────────────────────────────

  /**
   * Register the Matterport showcase window so that any interaction inside the
   * iframe (click, keydown, touchstart) can resume a suspended AudioContext.
   * Call this right after SDK connects, before Camera.pose fires.
   */
  setIframeWindow(iframeWin) {
    this._iframeWin = iframeWin;
  }

  /**
   * Open the audio start gate. Called from the "Start Tour" button on the
   * Welcome screen so background music / spatial zones only begin after
   * the user explicitly enters the tour. Replays any startAll() that was
   * deferred while the gate was closed, and unconditionally fires one
   * fresh startAll() so the ambient track begins even if no upstream
   * caller queued a request while the gate was closed.
   */
  allowStart() {
    if (this._startAllowed) return;
    this._startAllowed = true;
    this._pendingStart = false;
    console.log('🔊 AudioService: gate opened — starting ambient + spatial sources');
    this.startAll().catch(err =>
      console.warn('⚠️ AudioService allowStart→startAll failed:', err?.message)
    );
  }

  async startAll() {
    // Allow starting even with 0 spatial zones when ambient track is configured
    if (this._started) return;
    // Gate: do nothing until the user enters the tour. The pending flag
    // lets allowStart() replay this call once the gate opens.
    if (!this._startAllowed) {
      this._pendingStart = true;
      return;
    }
    const hasZones   = this._zones.length > 0;
    const hasAmbient = this._ambientConfig?.enabled;
    if (!hasZones && !hasAmbient) return;
    this._started = true;

    const ctx = this._getOrCreateCtx();
    this._masterGain = ctx.createGain();
    this._masterGain.gain.value = this._enabled ? this._globalVolume : 0;
    this._masterGain.connect(ctx.destination);

    // Try immediate resume — works if the page already has user activation
    // (e.g. the user clicked the Matterport viewer to load it).
    try { await ctx.resume(); } catch (_) {}

    // Belt-and-suspenders: register resume listeners on BOTH the parent document
    // AND the Matterport iframe window (same-origin), so the very first click/key
    // in the 3D viewer resumes the context even if no parent-page gesture happened.
    const resumeOnGesture = () => {
      if (this._audioCtx && this._audioCtx.state === 'suspended' && !this._muted) {
        this._audioCtx.resume().then(() => {
          console.log('🔊 AudioService: AudioContext resumed via gesture listener');
        }).catch(() => {});
      }
      document.removeEventListener('click', resumeOnGesture);
      document.removeEventListener('keydown', resumeOnGesture);
      document.removeEventListener('touchstart', resumeOnGesture);
      try {
        this._iframeWin?.removeEventListener('click', resumeOnGesture);
        this._iframeWin?.removeEventListener('keydown', resumeOnGesture);
        this._iframeWin?.removeEventListener('touchstart', resumeOnGesture);
      } catch (_) {}
    };
    document.addEventListener('click', resumeOnGesture);
    document.addEventListener('keydown', resumeOnGesture);
    document.addEventListener('touchstart', resumeOnGesture);
    if (this._iframeWin) {
      try {
        this._iframeWin.addEventListener('click', resumeOnGesture);
        this._iframeWin.addEventListener('keydown', resumeOnGesture);
        this._iframeWin.addEventListener('touchstart', resumeOnGesture);
      } catch (_) {}
    }

    // Start ambient FIRST — it doesn't depend on zone positions.
    await this._startAmbient(ctx);

    if (hasZones) {
      const results = await Promise.allSettled(
        this._zones.map(zone => this._startZoneSource(zone, ctx))
      );
      const started = results.filter(r => r.status === 'fulfilled').length;
      console.log(`🔊 AudioService: ${started}/${this._zones.length} spatial sources active`);
    }
  }

  /** Tear down all zone sources and restart them (used by reloadConfig). */
  async _restartZones() {
    // Tear down ambient before clearing sources
    this._teardownAmbient();

    for (const [, src] of this._sources) {
      try { src.osc1?.stop(); } catch (_) {}
      try { src.osc2?.stop(); } catch (_) {}
      try { src.bufferSource?.stop(); } catch (_) {}
    }
    this._sources.clear();

    const ctx = this._audioCtx;
    if (!ctx || ctx.state === 'closed') return;

    if (!this._masterGain) {
      this._masterGain = ctx.createGain();
      this._masterGain.gain.value = this._enabled ? this._globalVolume : 0;
      this._masterGain.connect(ctx.destination);
    }

    const results = await Promise.allSettled(
      this._zones.map(zone => this._startZoneSource(zone, ctx))
    );
    const started = results.filter(r => r.status === 'fulfilled').length;
    console.log(`🔊 AudioService: restarted ${started}/${this._zones.length} sources after config reload`);

    // Restart ambient after zone sources
    await this._startAmbient(ctx);
  }

  // ─── Ambient track ────────────────────────────────────────────────────────

  async _startAmbient(ctx) {
    const cfg = this._ambientConfig;
    if (!cfg || !cfg.enabled || !cfg.audioSrc) return;

    // On mobile we cannot decode the full track into a PCM AudioBuffer:
    // a 14 MB MP3 expands to ~150 MB of PCM and OOM-crashes iOS Safari.
    // Instead we stream it via a plain HTMLAudioElement (no WebAudio
    // routing — createMediaElementSource is unreliable on iOS Safari
    // and forces all output through the context, which can silently
    // mute everything). Volume ducking is implemented by ramping
    // element.volume directly in _duckAmbient/_unduckAmbient.
    // Force the desktop AudioBuffer pipeline on mobile too. The mobile
    // streaming path (HTMLAudioElement + MediaElementSource → GainNode)
    // produces near-silent or zero output on iPhone Chrome — the user
    // confirmed that "Request Desktop Site" (which flips this UA check
    // to false) makes ambient + narration audible. Decoded buffer
    // playback uses ~150 MB on a 14 MB MP3 — fine on iPhone 12+ but
    // may strain iPhone 11 / older 3 GB devices.
    const isMobile = false;
    if (isMobile) {
      try {
        const url = assetUrl(cfg.audioSrc);
        // Reuse the element created during loadConfig() (it has been
        // buffering the MP3 while the loading screen was visible).
        // Fall back to creating one here if loadConfig didn't run.
        let el = this._ambientHtmlEl;
        if (!el) {
          el = new Audio();
          el.loop = true;
          el.preload = 'auto';
          el.playsInline = true;
          el.setAttribute('playsinline', '');
          el.setAttribute('webkit-playsinline', '');
          el.src = url;
          this._ambientHtmlEl = el;
        }
        // Mobile speakers tend to emphasize the ambient track and feel
        // much louder than desktop, so apply an extra reduction factor.
        // gainTarget is the value applied to the MediaElementSource→GainNode
        // path (masterGain handles globalVolume), elTarget is for the
        // direct el.volume fallback path (no masterGain in chain).
        const gainTarget = this._mobileAmbientTarget(false, true);
        const elTarget   = this._mobileAmbientTarget(false, false);
        // The element may already be running muted (autoplay started in
        // loadConfig() to buffer during the welcome screen). Unmute it
        // and ramp volume up smoothly so the transition is graceful.
        try { el.muted = false; } catch (_) {}
        try { el.removeAttribute('muted'); } catch (_) {}

        // ── iOS volume fix ─────────────────────────────────────────────
        // iOS Safari treats HTMLMediaElement.volume as read-only — the
        // setter is silently ignored and the track always plays at the
        // device's system volume. The only reliable way to scale the
        // volume programmatically is to route the element through a
        // Web Audio MediaElementAudioSourceNode → GainNode. Once
        // connected, the GainNode's gain *does* attenuate playback on
        // iOS. We do this lazily inside _startAmbient so it happens
        // post-gesture (ctx is running) and at most once.
        if (!this._ambientMobileGain && this._masterGain && ctx && ctx.state === 'running') {
          try {
            const sourceNode = ctx.createMediaElementSource(el);
            const gainNode   = ctx.createGain();
            gainNode.gain.setValueAtTime(gainTarget, ctx.currentTime);
            sourceNode.connect(gainNode);
            gainNode.connect(this._masterGain);
            this._ambientMobileSource = sourceNode;
            this._ambientMobileGain   = gainNode;
            // Ensure the element output goes through Web Audio only.
            // Setting volume to 1 is the canonical pre-gain level
            // (the GainNode does the actual scaling). On Android
            // browsers that DO honour el.volume, this is fine because
            // GainNode is the dominant control after wiring.
            el.volume = 1;
            console.log(`🌿 AudioService: ambient routed through MediaElementSource → GainNode (iOS-safe), gain=${gainTarget.toFixed(3)}`);
          } catch (err) {
            // Older iOS versions or repeat-connect errors — fall back to
            // setting el.volume directly (works on Android, no-op on iOS).
            console.warn('🌿 AudioService: MediaElementSource wiring failed, using el.volume fallback:', err?.message);
            if (el.paused) el.volume = Math.max(0, Math.min(1, elTarget));
            else this._rampHtmlVolume(el, elTarget, 0.4);
          }
        } else if (this._ambientMobileGain) {
          // Already routed (e.g. after a reload) — ramp the gain.
          const g = this._ambientMobileGain.gain;
          const t = ctx.currentTime;
          g.cancelScheduledValues(t);
          g.setValueAtTime(g.value, t);
          g.linearRampToValueAtTime(gainTarget, t + 0.4);
        } else {
          // No AudioContext yet — fall back to the legacy path.
          if (el.paused) el.volume = Math.max(0, Math.min(1, elTarget));
          else this._rampHtmlVolume(el, elTarget, 0.4);
        }

        this._ambientSource = el; // truthy → isAmbientPlaying() returns true
        this._ambientDucked = false;

        const tryPlay = () => {
          const p = el.play();
          if (p && typeof p.then === 'function') {
            p.then(() => {
              console.log(`🌿 AudioService: ambient streaming on mobile — ${cfg.audioSrc}`);
              try {
                window.dispatchEvent(new CustomEvent('audioPlaying', {
                  detail: { source: 'ambient', playing: true },
                }));
              } catch (_) {}
            }).catch((err) => {
              console.warn('🌿 AudioService: ambient play() blocked:', err?.message);
              // Retry on the next user gesture.
              const retry = () => {
                document.removeEventListener('touchstart', retry, true);
                document.removeEventListener('click', retry, true);
                el.play().catch(() => {});
              };
              document.addEventListener('touchstart', retry, true);
              document.addEventListener('click', retry, true);
            });
          }
        };
        tryPlay();
      } catch (err) {
        console.warn('🌿 AudioService: mobile streaming ambient failed:', err?.message);
      }
      return;
    }

    try {
      // Prefer the prefetched ArrayBuffer (loaded at config-time) so we
      // skip the network round-trip after the user gesture. Fall back to
      // a fresh fetch only if the prefetch wasn't initiated or failed.
      let arrayBuffer = this._ambientArrayBuffer;
      if (!arrayBuffer) {
        if (this._ambientFetchPromise) {
          try { await this._ambientFetchPromise; } catch (_) {}
          arrayBuffer = this._ambientArrayBuffer;
        }
      }
      if (!arrayBuffer) {
        const response = await fetch(assetUrl(cfg.audioSrc));
        if (!response.ok) throw new Error(`HTTP ${response.status} for ${cfg.audioSrc}`);
        arrayBuffer = await response.arrayBuffer();
      }
      const audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));

      this._ambientGain = ctx.createGain();
      this._ambientGain.gain.setValueAtTime(cfg.volume ?? 0.4, ctx.currentTime);
      this._ambientGain.connect(this._masterGain);

      const createAndStartSource = () => {
        // Always build a fresh BufferSource — nodes can only be .start()ed once.
        try { this._ambientSource?.stop(); } catch (_) {}
        try { this._ambientSource?.disconnect(); } catch (_) {}
        this._ambientSource = ctx.createBufferSource();
        this._ambientSource.buffer = audioBuffer;
        this._ambientSource.loop   = true;
        this._ambientSource.connect(this._ambientGain);
        this._ambientSource.start(0);
        this._ambientDucked = false;
        console.log(`🌿 AudioService: ambient track playing — ${cfg.audioSrc}`);
        // Notify listeners (e.g. the header equalizer) that audio is now
        // actually producing sound. Until this fires, UI elements should
        // present a static/idle state rather than animating.
        try {
          window.dispatchEvent(new CustomEvent('audioPlaying', {
            detail: { source: 'ambient', playing: true },
          }));
        } catch (_) {}
      };

      if (ctx.state === 'running') {
        createAndStartSource();
      } else {
        // Mobile: context is still suspended (no user gesture yet). Starting
        // a BufferSource here would be silently consumed on iOS. Defer until
        // the context transitions to 'running' after the unlock gesture.
        console.log(`🌿 AudioService: ambient decoded, waiting for AudioContext to run — ${cfg.audioSrc}`);
        const onStateChange = () => {
          if (ctx.state === 'running') {
            ctx.removeEventListener('statechange', onStateChange);
            createAndStartSource();
          }
        };
        ctx.addEventListener('statechange', onStateChange);
      }
    } catch (err) {
      console.warn('🌿 AudioService: ambient track failed to load:', err?.message);
    }
  }

  _teardownAmbient() {
    try { this._ambientSource?.stop?.(); } catch (_) {}
    if (this._ambientHtmlEl) {
      try { this._ambientHtmlEl.pause(); } catch (_) {}
      try { this._ambientHtmlEl.removeAttribute('src'); this._ambientHtmlEl.load(); } catch (_) {}
      this._ambientHtmlEl = null;
    }
    if (this._ambientMobileGain) {
      try { this._ambientMobileGain.disconnect(); } catch (_) {}
      this._ambientMobileGain = null;
    }
    if (this._ambientMobileSource) {
      try { this._ambientMobileSource.disconnect(); } catch (_) {}
      this._ambientMobileSource = null;
    }
    if (this._ambientGain) {
      try { this._ambientGain.disconnect(); } catch (_) {}
      this._ambientGain = null;
    }
    this._ambientSource = null;
    this._ambientDucked = false;
    try {
      window.dispatchEvent(new CustomEvent('audioPlaying', {
        detail: { source: 'ambient', playing: false },
      }));
    } catch (_) {}
  }

  // ─── Zone sources ─────────────────────────────────────────────────────────

  _startZoneSource(zone, ctx) {
    // No PannerNode — distance-based gain is set manually in updateListener().
    // Audio graph: source → zoneGain → masterGain → destination
    const gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, ctx.currentTime); // start silent; updateListener() ramps to real gain
    gainNode.connect(this._masterGain);

    this._sources.set(zone.id, { gainNode, zone });

    if (zone.audioSrc === 'TEST_TONE') {
      return this._startTone(zone, ctx, gainNode);
    }
    return this._startFile(zone, ctx, gainNode);
  }

  // ─── TEST_TONE (oscillator drone) ─────────────────────────────────────────

  _startTone(zone, ctx, gainNode) {
    const freq = zone.toneHz ?? 110;
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = freq;
    osc2.frequency.value = freq * 1.004; // subtle beating
    osc1.connect(gainNode);
    osc2.connect(gainNode);
    osc1.start();
    osc2.start();

    const src = this._sources.get(zone.id);
    if (src) { src.osc1 = osc1; src.osc2 = osc2; }
    console.log(`🎵 Zone "${zone.label}": TEST_TONE ${freq} Hz at`, zone.position);
    return Promise.resolve();
  }

  // ─── File playback (fetch → decodeAudioData → AudioBufferSourceNode) ──────
  // Uses Web Audio API entirely — bypasses browser autoplay policy that blocks
  // HTMLAudioElement.play() when there has been no user gesture.

  async _startFile(zone, ctx, gainNode) {
    let response;
    try {
      response = await fetch(assetUrl(zone.audioSrc));
    } catch (err) {
      console.warn(`⚠️ AudioService: fetch failed for "${zone.label}" (${zone.audioSrc}):`, err?.message);
      throw err;
    }

    if (!response.ok) {
      const msg = `HTTP ${response.status} for "${zone.audioSrc}"`;
      console.warn(`⚠️ AudioService: ${msg} — check the file exists in public/assets/audio/`);
      throw new Error(msg);
    }

    const arrayBuffer = await response.arrayBuffer();

    let audioBuffer;
    try {
      audioBuffer = await ctx.decodeAudioData(arrayBuffer);
    } catch (err) {
      console.warn(`⚠️ AudioService: decodeAudioData failed for "${zone.label}":`, err?.message);
      throw err;
    }

    const bufferSource = ctx.createBufferSource();
    bufferSource.buffer = audioBuffer;
    bufferSource.loop   = zone.loop ?? true;
    bufferSource.connect(gainNode);
    bufferSource.start(0);

    const src = this._sources.get(zone.id);
    if (src) src.bufferSource = bufferSource;

    console.log(`🎵 Zone "${zone.label}": playing ${zone.audioSrc} at`, zone.position);
  }

  // ─── Listener position → manual distance-based gain (no PannerNode) ──────

  updateListener(position, rotation) {
    if (!this._audioCtx || this._audioCtx.state === 'closed') return;

    // Track for live config edits and reloadConfig()
    this._lastPosition = position;
    this._lastRotation = rotation;

    // Resume if suspended (Chrome autoplay policy) — but NOT while the
    // user has globally muted. setMuted(true) suspends the context, and
    // since updateListener() fires on every camera-pose tick, an
    // unconditional resume here re-enables audio within a frame or two
    // and the mute appears to "wear off" after a few seconds of movement.
    if (this._audioCtx.state === 'suspended' && !this._muted) {
      this._audioCtx.resume().then(() => {
        console.log('🔊 AudioService: AudioContext resumed on user interaction');
      }).catch(() => {});
    }

    // Freeze all gain ramping while a voiceover is playing — let it take full control
    if (this._voiceoverDucked) return;

    // Calculate distance to each zone and ramp its gain smoothly
    const now = this._audioCtx.currentTime;
    const RAMP_DURATION = 0.06; // 60ms — smooth enough to avoid clicks, fast enough to feel responsive
    const rampEnd = now + RAMP_DURATION;
    const ACTIVE_THRESHOLD = 0.05; // min gain to count as "playing"

    let topGain = 0;
    let topZoneId = null;

    const diagnostics = [];
    for (const [, src] of this._sources) {
      const z = src.zone;
      if (!z) continue;

      const dx = position.x - z.position.x;
      const dy = position.y - z.position.y;
      const dz = position.z - z.position.z;
      const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

      const ref   = z.refDistance   ?? 5;
      const max   = z.maxDistance   ?? 30;
      const roll  = z.rolloffFactor ?? 3;
      const zVol  = z.volume        ?? 0.5;

      let gain;
      if (dist <= ref) {
        gain = zVol;                                    // inside full-volume radius
      } else if (dist >= max) {
        gain = 0;                                       // beyond max → silent
      } else {
        gain = zVol * Math.pow(ref / dist, roll);       // exponential falloff
      }

      // Smooth ramp: cancel any pending ramp, anchor at current value, ramp to target
      const gp = src.gainNode.gain;
      gp.cancelScheduledValues(now);
      gp.setValueAtTime(gp.value, now);
      gp.linearRampToValueAtTime(gain, rampEnd);

      if (gain > topGain) {
        topGain = gain;
        topZoneId = z.id;
      }

      diagnostics.push(`${z.label} ${dist.toFixed(1)}m g=${gain.toFixed(3)}`);
    }

    // Duck / restore ambient track based on whether any zone is audible
    if (this._ambientGain && this._ambientConfig?.enabled) {
      const shouldDuck = topGain >= ACTIVE_THRESHOLD;
      if (shouldDuck !== this._ambientDucked) {
        this._ambientDucked = shouldDuck;
        // Don't restore ambient while tour narration is playing
        if (!shouldDuck && this._tourNarrationActive) return;
        const target   = shouldDuck
          ? (this._ambientConfig.duckedVolume ?? 0.05)
          : (this._ambientConfig.volume       ?? 0.4);
        const fadeTime = shouldDuck
          ? (this._ambientConfig.duckFadeTime   ?? 1.5)
          : (this._ambientConfig.unduckFadeTime ?? 2.5);
        const ag = this._ambientGain.gain;
        ag.cancelScheduledValues(now);
        ag.setValueAtTime(ag.value, now);
        ag.linearRampToValueAtTime(target, now + fadeTime);
        console.log(`🌿 Ambient: ${shouldDuck ? 'ducking' : 'restoring'} → ${target.toFixed(2)} over ${fadeTime}s`);
      }
    }

    // Notify listeners when the nearest active zone changes
    const newActiveId = topGain >= ACTIVE_THRESHOLD ? topZoneId : null;
    if (newActiveId !== this._currentActiveZoneId) {
      this._currentActiveZoneId = newActiveId;
      const activeZone = newActiveId ? this._zones.find(z => z.id === newActiveId) || null : null;
      this._notifyActiveZoneChange(activeZone);
    }

    // Log every 20th call (~2s at 100ms throttle) so we can confirm it's working
    this._logCounter = (this._logCounter || 0) + 1;
    if (this._logCounter % 20 === 1) {
      console.log(`🔊 Listener (${position.x.toFixed(1)}, ${position.y.toFixed(1)}, ${position.z.toFixed(1)}) — ${diagnostics.join(' | ')}`);
    }
  }

  // ─── Controls ─────────────────────────────────────────────────────────────

  setEnabled(enabled) {
    this._enabled = enabled;
    if (this._masterGain && this._audioCtx) {
      const target = enabled ? this._globalVolume : 0;
      const now = this._audioCtx.currentTime;
      this._masterGain.gain.cancelScheduledValues(now);
      this._masterGain.gain.setValueAtTime(this._masterGain.gain.value, now);
      this._masterGain.gain.linearRampToValueAtTime(target, now + 0.06);
    }
    console.log(`🔊 AudioService: ${enabled ? 'enabled' : 'muted'}`);
  }

  setGlobalVolume(vol) {
    this._globalVolume = Math.max(0, Math.min(1, vol));
    if (this._masterGain && this._enabled && this._audioCtx) {
      const now = this._audioCtx.currentTime;
      this._masterGain.gain.cancelScheduledValues(now);
      this._masterGain.gain.setValueAtTime(this._masterGain.gain.value, now);
      this._masterGain.gain.linearRampToValueAtTime(this._globalVolume, now + 0.06);
    }
    // Mobile ambient: when wired through MediaElementSource → GainNode,
    // it already routes via masterGain so the change above takes effect.
    // When the GainNode wiring is NOT in place yet (pre-gesture / iOS
    // fallback), mirror the change onto el.volume — works on Android,
    // no-op on iOS but harmless.
    if (this._ambientHtmlEl) {
      const target = this._mobileAmbientTarget(this._tourNarrationActive || this._voiceoverDucked, false);
      try { this._ambientHtmlEl.volume = target; } catch (_) {}
    }
    if (this._ambientMobileGain) {
      // Re-target the mobile gain to the new baseline.
      this._rampMobileAmbient(this._tourNarrationActive || this._voiceoverDucked, 0.06);
    }
  }

  stop() {
    // Tear down ambient first
    this._teardownAmbient();

    for (const [, src] of this._sources) {
      try { src.osc1?.stop(); } catch (_) {}
      try { src.osc2?.stop(); } catch (_) {}
      try { src.bufferSource?.stop(); } catch (_) {}
      if (src.audio) { src.audio.pause(); src.audio.src = ''; }
    }
    this._sources.clear();
    this._mediaSources.clear();
    if (this._audioCtx) {
      try { this._audioCtx.close(); } catch (_) {}
      this._audioCtx  = null;
      this._masterGain = null;
    }
    this._started = false;
    this._currentActiveZoneId = null;
    this._notifyActiveZoneChange(null);
    console.log('🔇 AudioService: stopped and torn down');
  }

  // ─── Video zone audio integration ─────────────────────────────────────────

  /**
   * Route a video element's audio through the shared AudioContext so that
   * audioService.updateListener() applies distance-based gain automatically.
   *
   * Must be called AFTER audioService.startAll() (needs _audioCtx + _masterGain).
   * The zone config shape is the same as locationVideoConfig.json zones — the
   * existing updateListener() distance math reads zone.refDistance / maxDistance.
   *
   * @param {object} zone          - Zone config (id, position, refDistance, maxDistance, rolloffFactor, volume)
   * @param {HTMLVideoElement} videoElement
   * @returns {boolean} true if registered successfully
   */
  registerVideoAudioZone(zone, videoElement) {
    // Lazily initialize AudioContext + masterGain here so that video audio works
    // even when startAll() was not called (e.g. no ambient audio zones configured,
    // or proximity fires before startAll() has a chance to run).
    const ctx = this._getOrCreateCtx();
    if (!this._masterGain) {
      this._masterGain = ctx.createGain();
      this._masterGain.gain.value = this._enabled ? this._globalVolume : 0;
      this._masterGain.connect(ctx.destination);
      console.log('🔊 AudioService: masterGain lazily created by registerVideoAudioZone');
    }

    // Avoid re-wiring an already-active zone.
    // Use a 'video:' prefix to avoid ID collisions with ambient audio zones in _sources.
    const sourceKey = 'video:' + zone.id;
    if (this._sources.has(sourceKey)) return true;

    try {
      const gainNode = ctx.createGain();
      gainNode.gain.setValueAtTime(0, ctx.currentTime); // updateListener() ramps to real gain
      gainNode.connect(this._masterGain);

      // Reuse cached MediaElementAudioSourceNode if this element was registered before.
      // createMediaElementSource() can only be called ONCE per element — subsequent calls
      // throw an InvalidStateError. We keep the node alive across unregister/re-register
      // cycles (unregister just disconnects its output, re-register reconnects it).
      let mediaSource = this._mediaSources.get(sourceKey);
      if (!mediaSource) {
        mediaSource = ctx.createMediaElementSource(videoElement);
        this._mediaSources.set(sourceKey, mediaSource);
      }
      mediaSource.connect(gainNode);

      // The video starts with muted=true (required for autoplay policy).
      // createMediaElementSource() disconnects the element's default speaker output,
      // so unmuting is safe (audio only flows through the Web Audio graph now).
      // Chrome skips audio frame decoding entirely for muted elements, so we MUST
      // unmute here or the MediaElementAudioSourceNode receives silence.
      videoElement.muted = false;

      this._sources.set(sourceKey, { gainNode, zone, mediaSource });

      // Immediately evaluate gain based on last known listener position
      if (this._lastPosition) {
        this.updateListener(this._lastPosition, this._lastRotation);
      }

      console.log(`🔊 AudioService: video zone "${zone.id}" registered for spatial audio`);
      return true;
    } catch (err) {
      console.warn(`🔊 AudioService: failed to register video zone "${zone.id}":`, err?.message);
      return false;
    }
  }

  /**
   * Remove a video zone from the audio graph (called when user leaves the zone).
   * The MediaElementAudioSourceNode is kept in _mediaSources so it can be reconnected
   * when the user re-enters without throwing on a second createMediaElementSource() call.
   * @param {string} zoneId
   */
  unregisterVideoAudioZone(zoneId) {
    const sourceKey = 'video:' + zoneId;
    const src = this._sources.get(sourceKey);
    if (!src) return;
    // Disconnect only the gainNode chain — mediaSource stays alive in _mediaSources
    try { src.mediaSource?.disconnect(); } catch (_) {}
    try { src.gainNode?.disconnect(); } catch (_) {}
    this._sources.delete(sourceKey);
    console.log(`🔊 AudioService: video zone "${zoneId}" unregistered from spatial audio`);
  }

  // ─── Voiceover playback ───────────────────────────────────────────────────

  /**
   * Play a voiceover audio file, smoothly ducking background music while it plays
   * and restoring it afterwards — creating a smooth, uninterrupted sacred atmosphere.
   *
   * If the file doesn't exist (404) the call is silently ignored so missing
   * voiceovers never break the background music.
   *
   * @param {string} src - Asset path, e.g. '/assets/audio/voiceover/tagId/mr.mp3'
   * @param {object} [opts]
   * @param {number} [opts.fadeOutDuration=2]    – seconds to duck background music
   * @param {number} [opts.fadeInDuration=3]     – seconds to restore background music
   * @param {number} [opts.voiceoverVolume=0.92] – peak gain of voiceover track
   * @param {number} [opts.bgDuckedLevel=0.04]   – ambient level during voiceover
   * @param {Function} [opts.onEnded]            – optional callback when voiceover finishes
   */
  async playVoiceover(src, opts = {}) {
    const {
      fadeOutDuration = 2,
      fadeInDuration  = 3,
      voiceoverVolume = 0.92,
      bgDuckedLevel   = 0.04,
      startOffset    = 0,
      onStart,
      onEnded,
    } = opts;

    // Pre-fetch and decode the audio BEFORE modifying the background music.
    // If the file is missing, we bail out cleanly without disturbing anything.
    const ctx = this._getOrCreateCtx();
    let buffer;
    try {
      const res = await fetch(assetUrl(src));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      buffer = await ctx.decodeAudioData(await res.arrayBuffer());
    } catch (err) {
      console.info(`🎙️ AudioService: voiceover not available for "${src}" — skipping (${err?.message})`);
      return;
    }

    // Stop any already-playing voiceover first
    this.stopVoiceover();

    if (!this._masterGain) {
      this._masterGain = ctx.createGain();
      this._masterGain.gain.value = this._enabled ? this._globalVolume : 0;
      this._masterGain.connect(ctx.destination);
    }

    const now = ctx.currentTime;
    this._voiceoverDucked = true;

    // ── 1. Smoothly duck ambient track ───────────────────────────────────
    if (this._ambientGain && this._ambientConfig?.enabled) {
      const ag = this._ambientGain.gain;
      ag.cancelScheduledValues(now);
      ag.setValueAtTime(ag.value, now);
      ag.linearRampToValueAtTime(bgDuckedLevel, now + fadeOutDuration);
    }
    // Mobile streaming path: ambient bypasses the AudioContext, so duck
    // its <audio> element volume directly to the mobile narration level.
    if (this._ambientHtmlEl && !this._ambientGain && this._ambientConfig?.enabled) {
      this._rampMobileAmbient(true, fadeOutDuration);
    }

    // ── 2. Silence all spatial zone sources ──────────────────────────────
    for (const [, zoneSrc] of this._sources) {
      if (zoneSrc.gainNode) {
        const g = zoneSrc.gainNode.gain;
        g.cancelScheduledValues(now);
        g.setValueAtTime(g.value, now);
        g.linearRampToValueAtTime(0, now + fadeOutDuration);
      }
    }

    // ── 3. Create voiceover gain node — start at full narration volume.
    // No fade-in / pre-roll: the narration begins at the same level it
    // sustains, so the opening words aren't lost in a low-volume ramp.
    const voGain = ctx.createGain();
    voGain.gain.setValueAtTime(voiceoverVolume, now);
    voGain.connect(this._masterGain);

    // ── 4. Create & start buffer source ──────────────────────────────────
    const voSource = ctx.createBufferSource();
    voSource.buffer = buffer;
    voSource.loop   = false;
    voSource.connect(voGain);
    // Clamp the start offset so we never seek past the end of the buffer.
    const safeOffset = Math.max(0, Math.min(startOffset || 0, Math.max(0, buffer.duration - 0.1)));
    voSource.start(0, safeOffset);
    this._voiceoverStartCtxTime = ctx.currentTime;
    this._voiceoverStartOffset  = safeOffset;
    if (typeof onStart === 'function') onStart();

    this._voiceoverSource = voSource;
    this._voiceoverGain   = voGain;

    console.log(`🎙️ AudioService: voiceover started — "${src}"`);

    // ── 5. Restore background music when voiceover ends ──────────────────
    voSource.onended = () => {
      // Guard: stopVoiceover() may have already reset this flag
      if (!this._voiceoverDucked) return;
      this._voiceoverDucked = false;

      const t = ctx.currentTime;

      // Fade voiceover gain out (already near-silent by this point, but be safe)
      if (this._voiceoverGain) {
        this._voiceoverGain.gain.cancelScheduledValues(t);
        this._voiceoverGain.gain.setValueAtTime(this._voiceoverGain.gain.value, t);
        this._voiceoverGain.gain.linearRampToValueAtTime(0, t + 0.5);
      }

      // Restore ambient back to its correct level
      if (this._ambientGain && this._ambientConfig?.enabled) {
        const target = this._ambientDucked
          ? (this._ambientConfig.duckedVolume ?? 0.05)
          : (this._ambientConfig.volume ?? 0.4);
        const ag = this._ambientGain.gain;
        ag.cancelScheduledValues(t);
        ag.setValueAtTime(ag.value, t);
        ag.linearRampToValueAtTime(target, t + fadeInDuration);
      }
      // Mobile streaming path: ramp the <audio> element back to baseline.
      if (this._ambientHtmlEl && !this._ambientGain && this._ambientConfig?.enabled) {
        this._rampMobileAmbient(false, fadeInDuration);
      }

      // Let updateListener restore spatial zones on the next camera movement
      if (this._lastPosition) {
        setTimeout(() => this.updateListener(this._lastPosition, this._lastRotation), 150);
      }

      this._voiceoverSource = null;
      this._voiceoverGain   = null;

      if (typeof onEnded === 'function') onEnded();
      console.log('🎙️ AudioService: voiceover ended — background music restored');
    };
  }

  /**
   * Returns how many seconds into the current voiceover buffer we are
   * right now (including any startOffset that the playback began at).
   * Returns 0 when no voiceover is active. Callers use this to capture
   * the resume position before calling stopVoiceover().
   */
  getVoiceoverElapsed() {
    if (!this._voiceoverSource || !this._audioCtx) return 0;
    const elapsed = (this._audioCtx.currentTime - this._voiceoverStartCtxTime)
                  + (this._voiceoverStartOffset || 0);
    return Math.max(0, elapsed);
  }

  /**
   * Returns the total duration (seconds) of the currently playing
   * voiceover buffer, or 0 when none is active. Used by the captions
   * read-along to time sentence segments against POI narrations that
   * use the Web Audio buffer path (no <audio> element to query).
   */
  getVoiceoverDuration() {
    const buf = this._voiceoverSource && this._voiceoverSource.buffer;
    return buf && isFinite(buf.duration) ? buf.duration : 0;
  }

  /** True while a POI voiceover buffer is actively playing. */
  isVoiceoverPlaying() {
    return !!(this._voiceoverSource && this._voiceoverDucked);
  }

  /**
   * Slowly fade out the current voiceover over `durationSec` and then stop it,
   * restoring background music. Used when the user walks away from a POI so
   * the narration tapers off instead of clipping abruptly. Preserves any
   * pending resume metadata — callers (voiceoverService) capture the elapsed
   * offset BEFORE invoking this so a quick return picks up where it left off.
   *
   * If a new voiceover starts (`playVoiceover` calls `stopVoiceover`) while a
   * fade-out is in progress, the scheduled cleanup is cancelled and the
   * fading source is hard-stopped — so no overlap can occur.
   */
  fadeOutVoiceover(durationSec = 2.5) {
    if (!this._voiceoverSource || !this._voiceoverGain || !this._audioCtx) return;
    if (this._voiceoverFading) return; // already fading

    // Disarm the natural-end callback so it doesn't double-restore or
    // clear the caller's resume cache when the buffer eventually stops.
    this._voiceoverDucked = false;
    this._voiceoverFading = true;

    const ctx    = this._audioCtx;
    const t      = ctx.currentTime;
    const src    = this._voiceoverSource;
    const gain   = this._voiceoverGain;
    const dur    = Math.max(0.1, durationSec);

    // Linear ramp the voiceover gain down to ~silence over `dur` seconds.
    const g = gain.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(0.0001, t + dur);

    // Restore ambient music in lockstep so the room "fills back in" as the
    // narration fades — feels like the user is naturally walking back into
    // the ambient soundscape.
    if (this._ambientGain && this._ambientConfig?.enabled) {
      const target = this._ambientDucked
        ? (this._ambientConfig.duckedVolume ?? 0.05)
        : (this._ambientConfig.volume ?? 0.4);
      const ag = this._ambientGain.gain;
      ag.cancelScheduledValues(t);
      ag.setValueAtTime(ag.value, t);
      ag.linearRampToValueAtTime(target, t + dur);
    }
    // Mobile streaming path: restore <audio> element baseline in lockstep.
    if (this._ambientHtmlEl && !this._ambientGain && this._ambientConfig?.enabled) {
      this._rampMobileAmbient(false, dur);
    }

    // Schedule the hard stop + cleanup. Tracked so a new playVoiceover()
    // can cancel it before the timeout fires.
    const timer = setTimeout(() => {
      this._voiceoverFadeTimer = null;
      this._voiceoverFading = false;
      try { src.stop(); } catch (_) {}
      // Only null out if no newer source replaced us in the meantime.
      if (this._voiceoverSource === src) {
        this._voiceoverSource = null;
        this._voiceoverGain   = null;
      }
      // Let updateListener restore spatial zones for the user's current pose.
      if (this._lastPosition) {
        this.updateListener(this._lastPosition, this._lastRotation);
      }
      console.log('🎙️ AudioService: voiceover fade-out complete');
    }, dur * 1000 + 60);
    this._voiceoverFadeTimer = timer;

    console.log(`🎙️ AudioService: fading voiceover out over ${dur.toFixed(1)}s`);
  }

  /**
   * Stop the current voiceover immediately and restore background music.
   */
  stopVoiceover() {
    if (!this._voiceoverSource && !this._voiceoverDucked && !this._voiceoverFading) return;

    // Cancel any in-flight slow fade-out so the cleanup doesn't double-fire.
    if (this._voiceoverFadeTimer) {
      clearTimeout(this._voiceoverFadeTimer);
      this._voiceoverFadeTimer = null;
    }
    this._voiceoverFading = false;

    // Disarm the onended handler so it doesn't double-restore
    this._voiceoverDucked = false;

    if (this._voiceoverSource) {
      if (this._voiceoverGain) {
        // Quick fade-out to avoid a click on abrupt stop
        const ctx = this._audioCtx;
        if (ctx) {
          const t = ctx.currentTime;
          this._voiceoverGain.gain.cancelScheduledValues(t);
          this._voiceoverGain.gain.setValueAtTime(this._voiceoverGain.gain.value, t);
          this._voiceoverGain.gain.linearRampToValueAtTime(0, t + 0.12);
        }
      }
      try { this._voiceoverSource.stop(); } catch (_) {}
      this._voiceoverSource = null;
      this._voiceoverGain   = null;
    }

    // Restore ambient
    if (this._audioCtx && this._ambientGain && this._ambientConfig?.enabled) {
      const t = this._audioCtx.currentTime;
      const target = this._ambientDucked
        ? (this._ambientConfig.duckedVolume ?? 0.05)
        : (this._ambientConfig.volume ?? 0.4);
      const ag = this._ambientGain.gain;
      ag.cancelScheduledValues(t);
      ag.setValueAtTime(ag.value, t);
      ag.linearRampToValueAtTime(target, t + 2.5);
    }
    // Mobile streaming path: ramp the <audio> element back to baseline.
    if (this._ambientHtmlEl && !this._ambientGain && this._ambientConfig?.enabled) {
      this._rampMobileAmbient(false, 2.5);
    }

    // Restore spatial zones via updateListener
    if (this._lastPosition) {
      setTimeout(() => this.updateListener(this._lastPosition, this._lastRotation), 150);
    }

    console.log('🎙️ AudioService: voiceover stopped — background music restored');
  }

  /**
   * Call from a real user gesture handler (e.g. Welcome modal "Start Tour"
   * button) to unlock Web Audio on mobile browsers. This creates/resumes the
   * AudioContext inside the gesture call stack so iOS Safari and Android
   * Chrome allow subsequent autoplay. Safe to call multiple times.
   */
  unlockOnGesture() {
    try {
      const ctx = this._getOrCreateCtx();
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
      }
      // Play a one-sample silent buffer — this is the classic iOS
      // "prime the pump" trick that guarantees the context is fully unlocked.
      const buf = ctx.createBuffer(1, 1, 22050);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      src.connect(ctx.destination);
      try { src.start(0); } catch (_) {}

      // iOS Safari audio-session workaround: by default WebAudio output is
      // routed through the "Ambient" audio session, which respects the
      // hardware ringer/silent switch. Playing a real HTMLAudioElement
      // (with playsInline) once on a user gesture switches Safari to the
      // "Playback" session, which IGNORES the silent switch — matching
      // the behaviour of native music apps. Without this, users with the
      // side switch flipped to silent hear nothing even though the
      // AudioContext is unlocked. Idempotent — only does work once.
      if (!this._iosSessionUnlocked) {
        try {
          const a = new Audio(
            // 0.05s of silence — minimal MP3 payload, enough to flip the
            // audio session category on iOS Safari.
            'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQxAADB8AhSmxhIIEVCSiJrDCQBTcu3UrAIwUdkRgQbFAZC1CQEwTJ9mjRvBA4UOLD8nKVOWfh+UlK3z/177OXrfOdKl7pyn3Xf//FJAhBwIRP///PPP//1nRTBAo4QAFAGgIRn///+7HTQXffqAA7VAA7VgIA',
          );
          a.preload = 'auto';
          a.playsInline = true;
          a.setAttribute('playsinline', '');
          a.setAttribute('webkit-playsinline', '');
          a.muted = false;
          a.volume = 0;
          const p = a.play();
          if (p && typeof p.then === 'function') {
            p.then(() => { this._iosSessionUnlocked = true; })
             .catch(() => { /* user gesture missing — try again next time */ });
          } else {
            this._iosSessionUnlocked = true;
          }
        } catch (_) { /* noop */ }
      }
    } catch (err) {
      console.warn('AudioService: unlockOnGesture failed', err?.message);
    }
  }

  /**
   * Pre-create and "prime" the HTMLAudioElement used for tour narration
   * INSIDE a user-gesture call stack. Must be invoked from the Start
   * Guided Tour click handler.
   *
   * Why this is required (mobile, especially iOS Safari):
   *   - playTourNarration() lazily creates _narrationEl on first use.
   *   - The first narration usually plays after a 1-5 s camera fly,
   *     long after the original tap's user-activation window has
   *     expired. iOS then rejects audio.play() with NotAllowedError
   *     and narration is silent.
   *   - Calling play() once on an HTMLAudioElement during the gesture
   *     "consents" that element to autoplay forever after on iOS,
   *     even when subsequently swapping its src and calling play()
   *     much later from a non-gesture context.
   *
   * Idempotent — safe to call on every tap.
   */
  primeTourNarration() {
    try {
      // Make sure the AudioContext is alive and the masterGain exists,
      // since playTourNarration() routes the element through them.
      const ctx = this._getOrCreateCtx();
      if (!this._masterGain && ctx) {
        this._masterGain = ctx.createGain();
        this._masterGain.gain.value = this._enabled ? this._globalVolume : 0;
        this._masterGain.connect(ctx.destination);
      }
      if (!this._narrationEl) {
        const el = new Audio();
        el.crossOrigin = 'anonymous';
        el.preload = 'auto';
        el.playsInline = true;
        el.setAttribute('playsinline', '');
        el.setAttribute('webkit-playsinline', '');
        this._narrationEl = el;
        try {
          this._narrationSource = ctx.createMediaElementSource(el);
          this._narrationSource.connect(this._masterGain);
        } catch (_) { /* element already wired */ }
      }
      // "Consent" the element by issuing a play()/pause() pair inside
      // the gesture stack. With no src this is a no-op audibly but
      // satisfies iOS Safari's autoplay policy for future src swaps.
      const el = this._narrationEl;
      try {
        const p = el.play();
        if (p && typeof p.then === 'function') {
          p.then(() => { try { el.pause(); } catch (_) {} })
           .catch(() => { /* expected when no src */ });
        }
      } catch (_) { /* noop */ }
    } catch (err) {
      console.warn('AudioService: primeTourNarration failed', err?.message);
    }
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  _getOrCreateCtx() {
    if (!this._audioCtx || this._audioCtx.state === 'closed') {
      this._audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Don't auto-resume while globally muted — setMuted(true) deliberately
    // suspends the context to silence all Web Audio sources together.
    if (this._audioCtx.state === 'suspended' && !this._muted) {
      this._audioCtx.resume().catch(() => {});
    }
    return this._audioCtx;
  }
}

const audioService = new AudioService();
export default audioService;
